import * as z from 'zod';

export const DuplicateItemRequest = z.object({
  _id: z
    .string()
    .describe('Item id to be duplicated.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({ businessLocationIds: z.array(z.string()).max(100).optional() })
    .optional(),
});
export const DuplicateItemResponse = z.object({
  itemId: z.array(z.string()).optional(),
});
export const CreateItemRequest = z.object({
  item: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the item was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the item was updated.')
          .optional()
          .nullable(),
        name: z.string().describe('Item name.').min(1).max(200).optional(),
        description: z
          .string()
          .describe('Item description.')
          .max(1500)
          .optional()
          .nullable(),
        image: z.string().describe('Main item image.').optional(),
        additionalImages: z.array(z.string()).max(15).optional(),
        labels: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Item label ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
            })
          )
          .max(10)
          .optional(),
        visible: z
          .boolean()
          .describe(
            'Whether the item is visible in the menu for site visitors.'
          )
          .optional()
          .nullable(),
        featured: z
          .boolean()
          .describe(
            'Whether the item is marked as featured/best seller.\nDefault: `false`.'
          )
          .optional()
          .nullable(),
        orderSettings: z
          .object({
            inStock: z
              .boolean()
              .describe('Whether the item is in stock.\nDefault: `true`.')
              .optional()
              .nullable(),
            acceptSpecialRequests: z
              .boolean()
              .describe(
                'Whether a customer can add a special request when ordering this item.\nDefault: `true`.'
              )
              .optional()
              .nullable(),
          })
          .describe('Online order settings.')
          .optional(),
        modifierGroups: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Modifier group ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            })
          )
          .max(25)
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        businessLocationIds: z.array(z.string()).max(100).optional(),
      }),
      z.xor([
        z.object({
          priceVariants: z.never().optional(),
          priceInfo: z.never().optional(),
        }),
        z.object({
          priceInfo: z.never().optional(),
          priceVariants: z
            .object({
              variants: z
                .array(
                  z.object({
                    variantId: z
                      .string()
                      .describe('Price variant ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    price: z
                      .string()
                      .describe('Price of a variant.')
                      .optional()
                      .nullable(),
                    priceInfo: z
                      .object({
                        price: z.string().describe('Price.').optional(),
                      })
                      .describe('Price info of a variant.')
                      .optional(),
                  })
                )
                .max(500)
                .optional(),
            })
            .describe('Item price variants.'),
        }),
        z.object({
          priceVariants: z.never().optional(),
          priceInfo: z
            .object({ price: z.string().describe('Price.').optional() })
            .describe('Item price info.'),
        }),
      ])
    )
    .describe('Item details.'),
});
export const CreateItemResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Item ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the item was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the item was updated.')
      .optional()
      .nullable(),
    name: z.string().describe('Item name.').min(1).max(200).optional(),
    description: z
      .string()
      .describe('Item description.')
      .max(1500)
      .optional()
      .nullable(),
    image: z.string().describe('Main item image.').optional(),
    additionalImages: z.array(z.string()).max(15).optional(),
    labels: z
      .array(
        z.object({
          _id: z
            .string()
            .describe('Item label ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
      )
      .max(10)
      .optional(),
    visible: z
      .boolean()
      .describe('Whether the item is visible in the menu for site visitors.')
      .optional()
      .nullable(),
    featured: z
      .boolean()
      .describe(
        'Whether the item is marked as featured/best seller.\nDefault: `false`.'
      )
      .optional()
      .nullable(),
    orderSettings: z
      .object({
        inStock: z
          .boolean()
          .describe('Whether the item is in stock.\nDefault: `true`.')
          .optional()
          .nullable(),
        acceptSpecialRequests: z
          .boolean()
          .describe(
            'Whether a customer can add a special request when ordering this item.\nDefault: `true`.'
          )
          .optional()
          .nullable(),
      })
      .describe('Online order settings.')
      .optional(),
    modifierGroups: z
      .array(
        z.object({
          _id: z
            .string()
            .describe('Modifier group ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
      )
      .max(25)
      .optional(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe('Extended fields.')
      .optional(),
    businessLocationIds: z.array(z.string()).max(100).optional(),
  }),
  z.xor([
    z.object({
      priceVariants: z.never().optional(),
      priceInfo: z.never().optional(),
    }),
    z.object({
      priceInfo: z.never().optional(),
      priceVariants: z
        .object({
          variants: z
            .array(
              z.object({
                variantId: z
                  .string()
                  .describe('Price variant ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                price: z
                  .string()
                  .describe('Price of a variant.')
                  .optional()
                  .nullable(),
                priceInfo: z
                  .object({ price: z.string().describe('Price.').optional() })
                  .describe('Price info of a variant.')
                  .optional(),
              })
            )
            .max(500)
            .optional(),
        })
        .describe('Item price variants.'),
    }),
    z.object({
      priceVariants: z.never().optional(),
      priceInfo: z
        .object({ price: z.string().describe('Price.').optional() })
        .describe('Item price info.'),
    }),
  ])
);
export const BulkCreateItemsRequest = z.object({
  items: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Item ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the item was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the item was updated.')
            .optional()
            .nullable(),
          name: z.string().describe('Item name.').min(1).max(200).optional(),
          description: z
            .string()
            .describe('Item description.')
            .max(1500)
            .optional()
            .nullable(),
          image: z.string().describe('Main item image.').optional(),
          additionalImages: z.array(z.string()).max(15).optional(),
          labels: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Item label ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
            )
            .max(10)
            .optional(),
          visible: z
            .boolean()
            .describe(
              'Whether the item is visible in the menu for site visitors.'
            )
            .optional()
            .nullable(),
          featured: z
            .boolean()
            .describe(
              'Whether the item is marked as featured/best seller.\nDefault: `false`.'
            )
            .optional()
            .nullable(),
          orderSettings: z
            .object({
              inStock: z
                .boolean()
                .describe('Whether the item is in stock.\nDefault: `true`.')
                .optional()
                .nullable(),
              acceptSpecialRequests: z
                .boolean()
                .describe(
                  'Whether a customer can add a special request when ordering this item.\nDefault: `true`.'
                )
                .optional()
                .nullable(),
            })
            .describe('Online order settings.')
            .optional(),
          modifierGroups: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Modifier group ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              })
            )
            .max(25)
            .optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe('Extended fields.')
            .optional(),
          businessLocationIds: z.array(z.string()).max(100).optional(),
        }),
        z.xor([
          z.object({
            priceVariants: z.never().optional(),
            priceInfo: z.never().optional(),
          }),
          z.object({
            priceInfo: z.never().optional(),
            priceVariants: z
              .object({
                variants: z
                  .array(
                    z.object({
                      variantId: z
                        .string()
                        .describe('Price variant ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      price: z
                        .string()
                        .describe('Price of a variant.')
                        .optional()
                        .nullable(),
                      priceInfo: z
                        .object({
                          price: z.string().describe('Price.').optional(),
                        })
                        .describe('Price info of a variant.')
                        .optional(),
                    })
                  )
                  .max(500)
                  .optional(),
              })
              .describe('Item price variants.'),
          }),
          z.object({
            priceVariants: z.never().optional(),
            priceInfo: z
              .object({ price: z.string().describe('Price.').optional() })
              .describe('Item price info.'),
          }),
        ])
      )
    )
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to return entity in the response.')
        .optional(),
    })
    .optional(),
});
export const BulkCreateItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for item update.')
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Item ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the item was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the item was updated.')
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Item name.')
                .min(1)
                .max(200)
                .optional(),
              description: z
                .string()
                .describe('Item description.')
                .max(1500)
                .optional()
                .nullable(),
              image: z.string().describe('Main item image.').optional(),
              additionalImages: z.array(z.string()).max(15).optional(),
              labels: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Item label ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  })
                )
                .max(10)
                .optional(),
              visible: z
                .boolean()
                .describe(
                  'Whether the item is visible in the menu for site visitors.'
                )
                .optional()
                .nullable(),
              featured: z
                .boolean()
                .describe(
                  'Whether the item is marked as featured/best seller.\nDefault: `false`.'
                )
                .optional()
                .nullable(),
              orderSettings: z
                .object({
                  inStock: z
                    .boolean()
                    .describe('Whether the item is in stock.\nDefault: `true`.')
                    .optional()
                    .nullable(),
                  acceptSpecialRequests: z
                    .boolean()
                    .describe(
                      'Whether a customer can add a special request when ordering this item.\nDefault: `true`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Online order settings.')
                .optional(),
              modifierGroups: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Modifier group ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(25)
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe('Extended fields.')
                .optional(),
              businessLocationIds: z.array(z.string()).max(100).optional(),
            }),
            z.xor([
              z.object({
                priceVariants: z.never().optional(),
                priceInfo: z.never().optional(),
              }),
              z.object({
                priceInfo: z.never().optional(),
                priceVariants: z
                  .object({
                    variants: z
                      .array(
                        z.object({
                          variantId: z
                            .string()
                            .describe('Price variant ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          price: z
                            .string()
                            .describe('Price of a variant.')
                            .optional()
                            .nullable(),
                          priceInfo: z
                            .object({
                              price: z.string().describe('Price.').optional(),
                            })
                            .describe('Price info of a variant.')
                            .optional(),
                        })
                      )
                      .max(500)
                      .optional(),
                  })
                  .describe('Item price variants.'),
              }),
              z.object({
                priceVariants: z.never().optional(),
                priceInfo: z
                  .object({ price: z.string().describe('Price.').optional() })
                  .describe('Item price info.'),
              }),
            ])
          )
          .describe('Created item.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the API call.')
    .optional(),
});
export const GetItemRequest = z.object({
  itemId: z
    .string()
    .describe('Item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetItemResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Item ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the item was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the item was updated.')
      .optional()
      .nullable(),
    name: z.string().describe('Item name.').min(1).max(200).optional(),
    description: z
      .string()
      .describe('Item description.')
      .max(1500)
      .optional()
      .nullable(),
    image: z.string().describe('Main item image.').optional(),
    additionalImages: z.array(z.string()).max(15).optional(),
    labels: z
      .array(
        z.object({
          _id: z
            .string()
            .describe('Item label ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
      )
      .max(10)
      .optional(),
    visible: z
      .boolean()
      .describe('Whether the item is visible in the menu for site visitors.')
      .optional()
      .nullable(),
    featured: z
      .boolean()
      .describe(
        'Whether the item is marked as featured/best seller.\nDefault: `false`.'
      )
      .optional()
      .nullable(),
    orderSettings: z
      .object({
        inStock: z
          .boolean()
          .describe('Whether the item is in stock.\nDefault: `true`.')
          .optional()
          .nullable(),
        acceptSpecialRequests: z
          .boolean()
          .describe(
            'Whether a customer can add a special request when ordering this item.\nDefault: `true`.'
          )
          .optional()
          .nullable(),
      })
      .describe('Online order settings.')
      .optional(),
    modifierGroups: z
      .array(
        z.object({
          _id: z
            .string()
            .describe('Modifier group ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
      )
      .max(25)
      .optional(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe('Extended fields.')
      .optional(),
    businessLocationIds: z.array(z.string()).max(100).optional(),
  }),
  z.xor([
    z.object({
      priceVariants: z.never().optional(),
      priceInfo: z.never().optional(),
    }),
    z.object({
      priceInfo: z.never().optional(),
      priceVariants: z
        .object({
          variants: z
            .array(
              z.object({
                variantId: z
                  .string()
                  .describe('Price variant ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                price: z
                  .string()
                  .describe('Price of a variant.')
                  .optional()
                  .nullable(),
                priceInfo: z
                  .object({ price: z.string().describe('Price.').optional() })
                  .describe('Price info of a variant.')
                  .optional(),
              })
            )
            .max(500)
            .optional(),
        })
        .describe('Item price variants.'),
    }),
    z.object({
      priceVariants: z.never().optional(),
      priceInfo: z
        .object({ price: z.string().describe('Price.').optional() })
        .describe('Item price info.'),
    }),
  ])
);
export const ListItemsRequest = z.object({
  options: z
    .object({
      itemIds: z.array(z.string()).optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .max(500)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .optional()
            .nullable(),
        })
        .describe('The metadata of the paginated results.')
        .optional(),
      onlyVisible: z
        .boolean()
        .describe(
          'Whether to return only items that are visible to site visitors.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const ListItemsResponse = z.object({
  items: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Item ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the item was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the item was updated.')
            .optional()
            .nullable(),
          name: z.string().describe('Item name.').min(1).max(200).optional(),
          description: z
            .string()
            .describe('Item description.')
            .max(1500)
            .optional()
            .nullable(),
          image: z.string().describe('Main item image.').optional(),
          additionalImages: z.array(z.string()).max(15).optional(),
          labels: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Item label ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
            )
            .max(10)
            .optional(),
          visible: z
            .boolean()
            .describe(
              'Whether the item is visible in the menu for site visitors.'
            )
            .optional()
            .nullable(),
          featured: z
            .boolean()
            .describe(
              'Whether the item is marked as featured/best seller.\nDefault: `false`.'
            )
            .optional()
            .nullable(),
          orderSettings: z
            .object({
              inStock: z
                .boolean()
                .describe('Whether the item is in stock.\nDefault: `true`.')
                .optional()
                .nullable(),
              acceptSpecialRequests: z
                .boolean()
                .describe(
                  'Whether a customer can add a special request when ordering this item.\nDefault: `true`.'
                )
                .optional()
                .nullable(),
            })
            .describe('Online order settings.')
            .optional(),
          modifierGroups: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Modifier group ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(25)
            .optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe('Extended fields.')
            .optional(),
          businessLocationIds: z.array(z.string()).max(100).optional(),
        }),
        z.xor([
          z.object({
            priceVariants: z.never().optional(),
            priceInfo: z.never().optional(),
          }),
          z.object({
            priceInfo: z.never().optional(),
            priceVariants: z
              .object({
                variants: z
                  .array(
                    z.object({
                      variantId: z
                        .string()
                        .describe('Price variant ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      price: z
                        .string()
                        .describe('Price of a variant.')
                        .optional()
                        .nullable(),
                      priceInfo: z
                        .object({
                          price: z.string().describe('Price.').optional(),
                        })
                        .describe('Price info of a variant.')
                        .optional(),
                    })
                  )
                  .max(500)
                  .optional(),
              })
              .describe('Item price variants.'),
          }),
          z.object({
            priceVariants: z.never().optional(),
            priceInfo: z
              .object({ price: z.string().describe('Price.').optional() })
              .describe('Item price info.'),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('The metadata of the paginated results.')
    .optional(),
});
export const QueryItemsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load.')
                .min(0)
                .max(500)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('Query options.'),
});
export const QueryItemsResponse = z.object({
  items: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Item ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the item was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the item was updated.')
            .optional()
            .nullable(),
          name: z.string().describe('Item name.').min(1).max(200).optional(),
          description: z
            .string()
            .describe('Item description.')
            .max(1500)
            .optional()
            .nullable(),
          image: z.string().describe('Main item image.').optional(),
          additionalImages: z.array(z.string()).max(15).optional(),
          labels: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Item label ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
            )
            .max(10)
            .optional(),
          visible: z
            .boolean()
            .describe(
              'Whether the item is visible in the menu for site visitors.'
            )
            .optional()
            .nullable(),
          featured: z
            .boolean()
            .describe(
              'Whether the item is marked as featured/best seller.\nDefault: `false`.'
            )
            .optional()
            .nullable(),
          orderSettings: z
            .object({
              inStock: z
                .boolean()
                .describe('Whether the item is in stock.\nDefault: `true`.')
                .optional()
                .nullable(),
              acceptSpecialRequests: z
                .boolean()
                .describe(
                  'Whether a customer can add a special request when ordering this item.\nDefault: `true`.'
                )
                .optional()
                .nullable(),
            })
            .describe('Online order settings.')
            .optional(),
          modifierGroups: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Modifier group ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(25)
            .optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe('Extended fields.')
            .optional(),
          businessLocationIds: z.array(z.string()).max(100).optional(),
        }),
        z.xor([
          z.object({
            priceVariants: z.never().optional(),
            priceInfo: z.never().optional(),
          }),
          z.object({
            priceInfo: z.never().optional(),
            priceVariants: z
              .object({
                variants: z
                  .array(
                    z.object({
                      variantId: z
                        .string()
                        .describe('Price variant ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      price: z
                        .string()
                        .describe('Price of a variant.')
                        .optional()
                        .nullable(),
                      priceInfo: z
                        .object({
                          price: z.string().describe('Price.').optional(),
                        })
                        .describe('Price info of a variant.')
                        .optional(),
                    })
                  )
                  .max(500)
                  .optional(),
              })
              .describe('Item price variants.'),
          }),
          z.object({
            priceVariants: z.never().optional(),
            priceInfo: z
              .object({ price: z.string().describe('Price.').optional() })
              .describe('Item price info.'),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('The metadata of the paginated results.')
    .optional(),
});
export const SearchItemsRequest = z.object({
  search: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about filtering ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language#the-filter-object) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters)).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .optional(),
        aggregations: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .string()
                  .describe(
                    'Aggregation name, returned in `aggregations.results.name`.'
                  )
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'UNKNOWN_AGGREGATION_TYPE',
                    'VALUE',
                    'RANGE',
                    'SCALAR',
                  ])
                  .optional(),
                fieldPath: z
                  .string()
                  .describe(
                    'Field to aggreg   ate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                }),
                z.object({
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  value: z
                    .object({
                      sortType: z
                        .enum(['COUNT', 'VALUE'])
                        .describe('Sort type.')
                        .optional(),
                      sortDirection: z
                        .enum(['DESC', 'ASC'])
                        .describe('Sort direction.')
                        .optional(),
                      limit: z
                        .number()
                        .int()
                        .describe(
                          'Number of aggregation results to return.\nMin: `1`\nMax: `250`\nDefault: `10`'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  scalar: z.never().optional(),
                  range: z
                    .object({
                      buckets: z
                        .array(
                          z.object({
                            from: z
                              .number()
                              .describe(
                                'Inclusive lower bound of the range. Required if `to` is not provided.'
                              )
                              .optional()
                              .nullable(),
                            to: z
                              .number()
                              .describe(
                                'Exclusive upper bound of the range. Required if `from` is not provided.'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .optional(),
                    })
                    .describe(
                      'A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z
                    .object({
                      type: z
                        .enum([
                          'UNKNOWN_SCALAR_TYPE',
                          'COUNT_DISTINCT',
                          'MIN',
                          'MAX',
                        ])
                        .describe('Type of scalar aggregation.')
                        .optional(),
                    })
                    .describe(
                      'A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`.'
                    ),
                }),
              ])
            )
          )
          .optional(),
        search: z
          .object({
            mode: z.enum(['OR', 'AND']).optional(),
            expression: z
              .string()
              .describe('Search term or expression.')
              .optional()
              .nullable(),
            fields: z.array(z.string()).optional(),
            fuzzy: z
              .boolean()
              .describe(
                'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
              )
              .optional(),
          })
          .describe('Free text to match in searchable fields.')
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load.')
                .min(0)
                .max(500)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about cursor paging ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language#cursor-paging) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging)).'
            ),
        }),
      ])
    )
    .describe('Search options.'),
});
export const SearchItemsResponse = z.object({
  items: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Item ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the item was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the item was updated.')
            .optional()
            .nullable(),
          name: z.string().describe('Item name.').min(1).max(200).optional(),
          description: z
            .string()
            .describe('Item description.')
            .max(1500)
            .optional()
            .nullable(),
          image: z.string().describe('Main item image.').optional(),
          additionalImages: z.array(z.string()).max(15).optional(),
          labels: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Item label ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
            )
            .max(10)
            .optional(),
          visible: z
            .boolean()
            .describe(
              'Whether the item is visible in the menu for site visitors.'
            )
            .optional()
            .nullable(),
          featured: z
            .boolean()
            .describe(
              'Whether the item is marked as featured/best seller.\nDefault: `false`.'
            )
            .optional()
            .nullable(),
          orderSettings: z
            .object({
              inStock: z
                .boolean()
                .describe('Whether the item is in stock.\nDefault: `true`.')
                .optional()
                .nullable(),
              acceptSpecialRequests: z
                .boolean()
                .describe(
                  'Whether a customer can add a special request when ordering this item.\nDefault: `true`.'
                )
                .optional()
                .nullable(),
            })
            .describe('Online order settings.')
            .optional(),
          modifierGroups: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Modifier group ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(25)
            .optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe('Extended fields.')
            .optional(),
          businessLocationIds: z.array(z.string()).max(100).optional(),
        }),
        z.xor([
          z.object({
            priceVariants: z.never().optional(),
            priceInfo: z.never().optional(),
          }),
          z.object({
            priceInfo: z.never().optional(),
            priceVariants: z
              .object({
                variants: z
                  .array(
                    z.object({
                      variantId: z
                        .string()
                        .describe('Price variant ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      price: z
                        .string()
                        .describe('Price of a variant.')
                        .optional()
                        .nullable(),
                      priceInfo: z
                        .object({
                          price: z.string().describe('Price.').optional(),
                        })
                        .describe('Price info of a variant.')
                        .optional(),
                    })
                  )
                  .max(500)
                  .optional(),
              })
              .describe('Item price variants.'),
          }),
          z.object({
            priceVariants: z.never().optional(),
            priceInfo: z
              .object({ price: z.string().describe('Price.').optional() })
              .describe('Item price info.'),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('The metadata of the paginated results.')
    .optional(),
  aggregationData: z
    .object({
      results: z
        .array(
          z.intersection(
            z.object({
              name: z
                .string()
                .describe(
                  'Aggregation name, returned in `aggregations.results.name`.'
                )
                .optional(),
              type: z
                .enum(['UNKNOWN_AGGREGATION_TYPE', 'VALUE', 'RANGE', 'SCALAR'])
                .describe(
                  'Type of aggregation to perform. Must align with the corresponding aggregation field.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
              }),
              z.object({
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                values: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Number of entities with this value.')
                            .optional(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                scalar: z.never().optional(),
                ranges: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          from: z
                            .number()
                            .describe('Inclusive lower bound of the range.')
                            .optional()
                            .nullable(),
                          to: z
                            .number()
                            .describe('Exclusive upper bound of the range.')
                            .optional()
                            .nullable(),
                          count: z
                            .number()
                            .int()
                            .describe('Total number of entities in this range.')
                            .optional(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Range aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z
                  .object({
                    type: z
                      .enum([
                        'UNKNOWN_SCALAR_TYPE',
                        'COUNT_DISTINCT',
                        'MIN',
                        'MAX',
                      ])
                      .describe('Type of scalar aggregation.')
                      .optional(),
                    value: z
                      .number()
                      .describe('Value of the scalar aggregation.')
                      .optional(),
                  })
                  .describe('Scalar aggregation results.'),
              }),
            ])
          )
        )
        .optional(),
    })
    .describe('The metadata of the search results.')
    .optional(),
});
export const CountItemsRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe('Filter for counting items.')
        .optional()
        .nullable(),
      searchDetails: z
        .object({
          mode: z.enum(['OR', 'AND']).optional(),
          expression: z
            .string()
            .describe('Search term or expression.')
            .optional()
            .nullable(),
          fields: z.array(z.string()).optional(),
          fuzzy: z
            .boolean()
            .describe(
              'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
            )
            .optional(),
        })
        .describe('Search details.')
        .optional(),
    })
    .optional(),
});
export const CountItemsResponse = z.object({
  count: z.number().int().describe('Counted items.').optional(),
});
export const UpdateItemRequest = z.object({
  _id: z
    .string()
    .describe('Item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  item: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.'
          ),
        _createdDate: z
          .date()
          .describe('Date and time the item was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the item was updated.')
          .optional()
          .nullable(),
        name: z.string().describe('Item name.').min(1).max(200).optional(),
        description: z
          .string()
          .describe('Item description.')
          .max(1500)
          .optional()
          .nullable(),
        image: z.string().describe('Main item image.').optional(),
        additionalImages: z.array(z.string()).max(15).optional(),
        labels: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Item label ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
            })
          )
          .max(10)
          .optional(),
        visible: z
          .boolean()
          .describe(
            'Whether the item is visible in the menu for site visitors.'
          )
          .optional()
          .nullable(),
        featured: z
          .boolean()
          .describe(
            'Whether the item is marked as featured/best seller.\nDefault: `false`.'
          )
          .optional()
          .nullable(),
        orderSettings: z
          .object({
            inStock: z
              .boolean()
              .describe('Whether the item is in stock.\nDefault: `true`.')
              .optional()
              .nullable(),
            acceptSpecialRequests: z
              .boolean()
              .describe(
                'Whether a customer can add a special request when ordering this item.\nDefault: `true`.'
              )
              .optional()
              .nullable(),
          })
          .describe('Online order settings.')
          .optional(),
        modifierGroups: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Modifier group ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            })
          )
          .max(25)
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        businessLocationIds: z.array(z.string()).max(100).optional(),
      }),
      z.xor([
        z.object({
          priceVariants: z.never().optional(),
          priceInfo: z.never().optional(),
        }),
        z.object({
          priceInfo: z.never().optional(),
          priceVariants: z
            .object({
              variants: z
                .array(
                  z.object({
                    variantId: z
                      .string()
                      .describe('Price variant ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    price: z
                      .string()
                      .describe('Price of a variant.')
                      .optional()
                      .nullable(),
                    priceInfo: z
                      .object({
                        price: z.string().describe('Price.').optional(),
                      })
                      .describe('Price info of a variant.')
                      .optional(),
                  })
                )
                .max(500)
                .optional(),
            })
            .describe('Item price variants.'),
        }),
        z.object({
          priceVariants: z.never().optional(),
          priceInfo: z
            .object({ price: z.string().describe('Price.').optional() })
            .describe('Item price info.'),
        }),
      ])
    )
    .describe('Item to update.'),
});
export const UpdateItemResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Item ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the item was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the item was updated.')
      .optional()
      .nullable(),
    name: z.string().describe('Item name.').min(1).max(200).optional(),
    description: z
      .string()
      .describe('Item description.')
      .max(1500)
      .optional()
      .nullable(),
    image: z.string().describe('Main item image.').optional(),
    additionalImages: z.array(z.string()).max(15).optional(),
    labels: z
      .array(
        z.object({
          _id: z
            .string()
            .describe('Item label ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
      )
      .max(10)
      .optional(),
    visible: z
      .boolean()
      .describe('Whether the item is visible in the menu for site visitors.')
      .optional()
      .nullable(),
    featured: z
      .boolean()
      .describe(
        'Whether the item is marked as featured/best seller.\nDefault: `false`.'
      )
      .optional()
      .nullable(),
    orderSettings: z
      .object({
        inStock: z
          .boolean()
          .describe('Whether the item is in stock.\nDefault: `true`.')
          .optional()
          .nullable(),
        acceptSpecialRequests: z
          .boolean()
          .describe(
            'Whether a customer can add a special request when ordering this item.\nDefault: `true`.'
          )
          .optional()
          .nullable(),
      })
      .describe('Online order settings.')
      .optional(),
    modifierGroups: z
      .array(
        z.object({
          _id: z
            .string()
            .describe('Modifier group ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
      )
      .max(25)
      .optional(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe('Extended fields.')
      .optional(),
    businessLocationIds: z.array(z.string()).max(100).optional(),
  }),
  z.xor([
    z.object({
      priceVariants: z.never().optional(),
      priceInfo: z.never().optional(),
    }),
    z.object({
      priceInfo: z.never().optional(),
      priceVariants: z
        .object({
          variants: z
            .array(
              z.object({
                variantId: z
                  .string()
                  .describe('Price variant ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                price: z
                  .string()
                  .describe('Price of a variant.')
                  .optional()
                  .nullable(),
                priceInfo: z
                  .object({ price: z.string().describe('Price.').optional() })
                  .describe('Price info of a variant.')
                  .optional(),
              })
            )
            .max(500)
            .optional(),
        })
        .describe('Item price variants.'),
    }),
    z.object({
      priceVariants: z.never().optional(),
      priceInfo: z
        .object({ price: z.string().describe('Price.').optional() })
        .describe('Item price info.'),
    }),
  ])
);
export const BulkUpdateItemRequest = z.object({
  items: z
    .array(
      z.object({
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Item ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.'
                ),
              _createdDate: z
                .date()
                .describe('Date and time the item was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the item was updated.')
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Item name.')
                .min(1)
                .max(200)
                .optional(),
              description: z
                .string()
                .describe('Item description.')
                .max(1500)
                .optional()
                .nullable(),
              image: z.string().describe('Main item image.').optional(),
              additionalImages: z.array(z.string()).max(15).optional(),
              labels: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Item label ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  })
                )
                .max(10)
                .optional(),
              visible: z
                .boolean()
                .describe(
                  'Whether the item is visible in the menu for site visitors.'
                )
                .optional()
                .nullable(),
              featured: z
                .boolean()
                .describe(
                  'Whether the item is marked as featured/best seller.\nDefault: `false`.'
                )
                .optional()
                .nullable(),
              orderSettings: z
                .object({
                  inStock: z
                    .boolean()
                    .describe('Whether the item is in stock.\nDefault: `true`.')
                    .optional()
                    .nullable(),
                  acceptSpecialRequests: z
                    .boolean()
                    .describe(
                      'Whether a customer can add a special request when ordering this item.\nDefault: `true`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Online order settings.')
                .optional(),
              modifierGroups: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Modifier group ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  })
                )
                .max(25)
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe('Extended fields.')
                .optional(),
              businessLocationIds: z.array(z.string()).max(100).optional(),
            }),
            z.xor([
              z.object({
                priceVariants: z.never().optional(),
                priceInfo: z.never().optional(),
              }),
              z.object({
                priceInfo: z.never().optional(),
                priceVariants: z
                  .object({
                    variants: z
                      .array(
                        z.object({
                          variantId: z
                            .string()
                            .describe('Price variant ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          price: z
                            .string()
                            .describe('Price of a variant.')
                            .optional()
                            .nullable(),
                          priceInfo: z
                            .object({
                              price: z.string().describe('Price.').optional(),
                            })
                            .describe('Price info of a variant.')
                            .optional(),
                        })
                      )
                      .max(500)
                      .optional(),
                  })
                  .describe('Item price variants.'),
              }),
              z.object({
                priceVariants: z.never().optional(),
                priceInfo: z
                  .object({ price: z.string().describe('Price.').optional() })
                  .describe('Item price info.'),
              }),
            ])
          )
          .describe('Item to update.')
          .optional(),
        mask: z.array(z.string()).optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to return entity in the response.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateItemResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for item update.')
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Item ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the item is updated. To prevent conflicting changes, the current revision must be passed when updating the item. Ignored when creating a item.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the item was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the item was updated.')
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Item name.')
                .min(1)
                .max(200)
                .optional(),
              description: z
                .string()
                .describe('Item description.')
                .max(1500)
                .optional()
                .nullable(),
              image: z.string().describe('Main item image.').optional(),
              additionalImages: z.array(z.string()).max(15).optional(),
              labels: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Item label ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  })
                )
                .max(10)
                .optional(),
              visible: z
                .boolean()
                .describe(
                  'Whether the item is visible in the menu for site visitors.'
                )
                .optional()
                .nullable(),
              featured: z
                .boolean()
                .describe(
                  'Whether the item is marked as featured/best seller.\nDefault: `false`.'
                )
                .optional()
                .nullable(),
              orderSettings: z
                .object({
                  inStock: z
                    .boolean()
                    .describe('Whether the item is in stock.\nDefault: `true`.')
                    .optional()
                    .nullable(),
                  acceptSpecialRequests: z
                    .boolean()
                    .describe(
                      'Whether a customer can add a special request when ordering this item.\nDefault: `true`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Online order settings.')
                .optional(),
              modifierGroups: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Modifier group ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(25)
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe('Extended fields.')
                .optional(),
              businessLocationIds: z.array(z.string()).max(100).optional(),
            }),
            z.xor([
              z.object({
                priceVariants: z.never().optional(),
                priceInfo: z.never().optional(),
              }),
              z.object({
                priceInfo: z.never().optional(),
                priceVariants: z
                  .object({
                    variants: z
                      .array(
                        z.object({
                          variantId: z
                            .string()
                            .describe('Price variant ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          price: z
                            .string()
                            .describe('Price of a variant.')
                            .optional()
                            .nullable(),
                          priceInfo: z
                            .object({
                              price: z.string().describe('Price.').optional(),
                            })
                            .describe('Price info of a variant.')
                            .optional(),
                        })
                      )
                      .max(500)
                      .optional(),
                  })
                  .describe('Item price variants.'),
              }),
              z.object({
                priceVariants: z.never().optional(),
                priceInfo: z
                  .object({ price: z.string().describe('Price.').optional() })
                  .describe('Item price info.'),
              }),
            ])
          )
          .describe(
            'Updated item. Only returned if `returnEntity` is set to `true`.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the API call.')
    .optional(),
});
export const DeleteItemRequest = z.object({
  itemId: z
    .string()
    .describe('Item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteItemResponse = z.object({});
export const BulkDeleteItemsRequest = z.object({ ids: z.array(z.string()) });
export const BulkDeleteItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for item delete.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the API call.')
    .optional(),
});
